"use client";

import React, { createContext, useContext } from "react";
import { DEFAULT_SITE_TYPE, type SiteType } from "@/config/siteTypes";
import type { ConsentDef } from "@/features/profile-consent/types";

/**
 * Feature flag mode. Uses string values to future-proof for partial modes
 * (e.g., "beta", "read-only") without schema changes.
 */
export type FeatureFlagMode = "enabled" | "disabled";

/**
 * Per-tenant feature flags controlled by super admins.
 */
export interface FeatureFlags {
    livescore: FeatureFlagMode;
    betslip: FeatureFlagMode;
    authentication: {
        enabled: boolean;
        anonymous: boolean;
        upgradeNudge: boolean;
    };
    worldCup: FeatureFlagMode;
    aiContentGeneration: FeatureFlagMode;
}

export type FeatureFlagName = keyof FeatureFlags;

/**
 * A named color from the tenant's design tokens.
 */
export interface NamedColor {
    name: string;
    value: string;
}

export type BetslipPosition =
    | "bottom-right"
    | "bottom-left"
    | "top-right"
    | "top-left"
    | "side-right"
    | "side-left";

/**
 * Betslip configuration for client components.
 */
export interface TenantBetslipConfig {
    mode: "disabled" | "predictions-only" | "predictions-and-odds";
    maxSelections: number;
    ctaUrlTemplate?: string | null;
    ctaLabel?: string | null;
    currency: string;
    stakePresets: number[];
    oddsPollingInterval?: number;
    /** Bookmaker ID from the FU odds integration. Pins the betslip to this operator. */
    operatorId?: string | null;
    /** Affiliate attribution tag substituted into operator URLs. */
    affiliateId?: string | null;
    /** Viewport anchor for the widget. */
    position: BetslipPosition;
    /** Operator logo override shown in the widget header. */
    brandingLogoUrl?: string | null;
    /** Override for the primary accent. Leave null to auto-adopt bookmaker color. */
    operatorPrimaryColor?: string | null;
}


/**
 * Gamification feature configuration for client components.
 */
export interface TenantGamificationConfig {
    /**
     * Master switch for gamification surfaces: Challenge Zone, quizzes/polls
     * on team & player pages, and `gamificationCard` blocks. Defaults to true.
     */
    enabled: boolean;
}

/**
 * Odds display mode. "flash" cycles odds and operator branding separately
 * (required by some regulatory jurisdictions). "default" shows them together.
 */
export type OddsDisplayMode = "default" | "flash";

/**
 * Odds display configuration for client components.
 */
export interface OddsDisplaySettings {
    displayMode: OddsDisplayMode;
    /** Flash animation cycle interval in milliseconds. */
    flashInterval: number;
    /** Preferred operator IDs in priority order (parsed from the CSV tenant field). */
    operators: string[];
    /** Default affiliate ID substituted into {affiliate_id} placeholders. */
    affiliateId: string | null;
    /** Per-operator affiliate overrides: operator_id → affiliate_id. */
    affiliateIds: Record<string, string>;
}

export type TeamLogoDisplay = "logos" | "country-flags" | "shield-fallback";

/**
 * Tenant settings that are relevant for client components.
 * This is a subset of the full Tenant type to avoid passing unnecessary data.
 */
export interface TenantSettings {
    /** Numeric tenant ID (string-cast for stable comparisons in client code). */
    tenantId: string | null;
    /** When true, player headshots are displayed. When false, shirt numbers are shown instead. */
    allowPlayerPhotos: boolean;
    /** Controls how team logos are displayed — real logos, country flags, or shield icons. */
    teamLogoDisplay: TeamLogoDisplay;
    /** The type of site - determines routing structure and available features. */
    siteType: SiteType;
    /** The fixed competition ID for this site (only for Football Competition Site type) */
    competitionId?: string | null;
    /** The fixed team ID for this site (only for Football Team Site type) */
    teamId?: string | null;
    /** Whether the tenant has multiple locales enabled (affects URL prefixing) */
    isMultiLang: boolean;
    /** When true and multi-lang, show a locale selector overlay on the homepage for first-time visitors. */
    promptLocaleOnFirstVisit: boolean;
    /** Leaderboard ID for Predictor game - when set, predictor features are enabled */
    predictorLeaderboardId?: string | null;
    /** Leaderboard ID for Top X game - when set, Top X features are enabled */
    topxLeaderboardId?: string | null;
    /** Leaderboard ID for Fantasy game - when set, Fantasy features are enabled */
    fantasyLeaderboardId?: string | null;
    /** Named color palette from design tokens */
    namedColors: NamedColor[];
    /** Body font family */
    fontFamily: string;
    /** Heading font family */
    fontFamilyHeading: string;
    /** Base font size */
    baseFontSize: string;
    /** Border radius */
    radius: string;
    /** Shadow style */
    shadowStyle: string;
    /** Spacing density */
    density: string;
    /** Betslip configuration for CTA integration */
    betslip?: TenantBetslipConfig;
    /** Controls how odds are displayed (default vs. flashing). */
    odds: OddsDisplaySettings;
    /**
     * Whether odds should be shown across the site. Driven by the tenant's
     * `features.odds.mode` enum — true only when the mode is `"enabled"`.
     * Independent of betslip mode. Surfaces that render odds should skip
     * them when this is false.
     */
    hasOdds: boolean;
    /** Default background image URL for gamification cards */
    gamificationDefaultBgUrl?: string | null;
    /** Gamification feature configuration */
    gamification: TenantGamificationConfig;
    /** Feature flags controlled by super admins */
    features: FeatureFlags;
    /** Cookie consent mode — 'disabled' or 'built-in' */
    consentMode: "disabled" | "built-in";
    /** Profile consent registry for sign-up, game gates, and profile settings */
    consents: ConsentDef[];
}

const TenantSettingsContext = createContext<TenantSettings | null>(null);

interface TenantSettingsProviderProps {
    settings: TenantSettings;
    children: React.ReactNode;
}

/**
 * Provider that makes tenant settings available to all client components.
 *
 * Usage in a server component:
 * ```tsx
 * import { getTenant } from "@/lib/tenant/getTenant";
 * import { TenantSettingsProvider, extractTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
 *
 * export default async function Page() {
 *     const tenant = await getTenant();
 *     const settings = extractTenantSettings(tenant);
 *
 *     return (
 *         <TenantSettingsProvider settings={settings}>
 *             <ClientComponent />
 *         </TenantSettingsProvider>
 *     );
 * }
 * ```
 */
export function TenantSettingsProvider({ settings, children }: TenantSettingsProviderProps) {
    return <TenantSettingsContext.Provider value={settings}>{children}</TenantSettingsContext.Provider>;
}

/**
 * Hook to access tenant settings in client components.
 * Must be used within a TenantSettingsProvider.
 *
 * Usage:
 * ```tsx
 * "use client";
 * import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
 *
 * function MyComponent() {
 *     const { allowPlayerPhotos } = useTenantSettings();
 *     // Use the settings...
 * }
 * ```
 */
export function useTenantSettings(): TenantSettings {
    const context = useContext(TenantSettingsContext);
    if (!context) {
        // Return default values if not within a provider (for backward compatibility)
        return {
            tenantId: null,
            allowPlayerPhotos: true,
            teamLogoDisplay: "logos" as TeamLogoDisplay,
            siteType: DEFAULT_SITE_TYPE,
            isMultiLang: false,
            promptLocaleOnFirstVisit: false,
            predictorLeaderboardId: null,
            topxLeaderboardId: null,
            fantasyLeaderboardId: null,
            namedColors: [],
            fontFamily: "Inter",
            fontFamilyHeading: "inherit",
            baseFontSize: "16px",
            radius: "0.5rem",
            shadowStyle: "subtle",
            density: "normal",
            betslip: undefined,
            odds: {
                displayMode: "default",
                flashInterval: 2000,
                operators: [],
                affiliateId: null,
                affiliateIds: {},
            },
            hasOdds: false,
            gamificationDefaultBgUrl: null,
            gamification: { enabled: true },
            features: {
                livescore: "disabled",
                betslip: "enabled",
                authentication: { enabled: true, anonymous: false, upgradeNudge: true },
                worldCup: "disabled",
                aiContentGeneration: "disabled",
            },
            consentMode: "disabled",
            consents: [],
        };
    }
    return context;
}

/**
 * Hook to check if a feature is enabled for the current tenant.
 * Returns true unless the flag is explicitly `"disabled"`.
 * For the `authentication` flag (object shape), returns `authentication.enabled`.
 */
export function useFeatureFlag(flag: FeatureFlagName): boolean {
    const { features } = useTenantSettings();
    const value = features[flag];
    if (flag === "authentication") {
        return (value as FeatureFlags["authentication"]).enabled;
    }
    return (value as FeatureFlagMode) !== "disabled";
}

/**
 * Hook to get the raw mode value for a string-valued feature flag.
 * Useful for future partial modes (e.g., "beta", "read-only").
 * Note: does not apply to the `authentication` flag — use `features.authentication.enabled` directly.
 */
export function useFeatureMode(flag: Exclude<FeatureFlagName, "authentication">): FeatureFlagMode {
    const { features } = useTenantSettings();
    return features[flag] as FeatureFlagMode;
}
